/**
 * EmployeePicker - Select an employee for employee referral source
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, User } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase } from '../../../shared/components/common';
import { getEntityUrl } from '../../../shared/utils/entityUrls';
import { useEmployees } from '../../../shared/people/hooks/useEmployees';
import type { EmployeeResponse } from '../../../generated/api/models/employeeResponse';

interface EmployeePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Whether to show a "None" option to clear selection */
  allowClear?: boolean;
  /** Show selected employee as a clickable link to its detail page */
  linkToEntity?: boolean;
}

const getEmployeeName = (emp: EmployeeResponse) => {
  if (emp.user?.first_name || emp.user?.last_name) {
    return `${emp.user.first_name || ''} ${emp.user.last_name || ''}`.trim();
  }
  return emp.user?.email || '';
};

export function EmployeePicker({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  allowClear = false,
  linkToEntity = false,
}: EmployeePickerProps) {
  const { t } = useTranslation('recruiting');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: employeesResponse } = useEmployees({
    employment_status: 'active',
    search: search || undefined,
    limit: 50,
  });

  const employees = useMemo<EmployeeResponse[]>(
    () => employeesResponse?.items ?? [],
    [employeesResponse?.items]
  );

  const handleSelect = useCallback((employeeId: string) => {
    onChange(employeeId === value ? null : employeeId);
    setOpen(false);
    setSearch('');
  }, [onChange, value]);

  const handleClear = useCallback(() => {
    onChange(null);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  return (
    <EntityPickerBase<EmployeeResponse>
      value={value || ''}
      items={employees}
      getItemLabel={getEmployeeName}
      renderItem={(emp, isSelected) => (
        <>
          <Check
            className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
          />
          <User className="me-2 h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col flex-1 min-w-0">
            <span className="truncate">{getEmployeeName(emp)}</span>
            {emp.current_job?.job_title_display && (
              <span className="text-muted-foreground text-xs truncate">
                {emp.current_job.job_title_display}
              </span>
            )}
          </div>
        </>
      )}
      placeholder={placeholder || t('candidates.placeholders.selectEmployee')}
      searchPlaceholder={t('candidates.placeholders.selectEmployee')}
      emptyMessage={t('common:app.noResults')}
      disabled={disabled}
      className={className}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={setOpen}
      onSelect={handleSelect}
      allowClear={allowClear}
      onClear={handleClear}
      hideAddNew
      addNewLabel=""
      onAddNew={() => {}}
      selectedEntityUrl={linkToEntity && value
        ? getEntityUrl({ entityType: 'people_employees', entityId: value })
        : undefined}
      entityIcon={linkToEntity ? User : undefined}
    />
  );
}
